<template>
  <div v-if="productType" class="product-details">
    <div class="header">
      <h2>
        {{ $t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT_DETAILS.HEADING') }}
      </h2>
      <a @click="emit('close')">
        <Icon icon="close" />
      </a>
    </div>
    <div class="images">
      <div v-if="selectedImage" class="main-image">
        <img :src="selectedImage.img" alt="" />
      </div>

      <div v-if="images.length > 1" class="alt-images">
        <button
          v-for="(image, index) in images"
          :key="index"
          class="alt-image"
          :class="{ selected: selectedView === image.viewId }"
          @click="selectView(image.viewId)"
        >
          <img :src="image.img" alt="" />
        </button>
      </div>
    </div>
    <div class="meta-data">
      <h4 class="name">{{ productTypeName }}</h4>
      <p class="brand">{{ productType.brandName }}</p>
      <h4 v-if="price" class="price">
        {{ sprdCurrency(price.amount, price.currencyId) }}
        <Icon
          v-tooltip="{
            content: $t(
              'PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT_DETAILS.PRICE_HINT',
            ),
          }"
          class="link-main icon-link"
          icon="question"
        />
      </h4>
    </div>
    <button
      class="btn btn-primary btn-block select-button"
      @click="emit('select')"
    >
      {{ $t('GENERAL.SELECT') }}
    </button>

    <div v-if="productType.appearanceIds?.length" class="colors">
      <strong>
        {{ $t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT_DETAILS.COLORS') }}
      </strong>
      <div class="color-circles">
        <ColorCircle
          v-for="appearance in appearances"
          :key="appearance.id"
          :color="appearance.color"
          :size="24"
          :name="appearance.name"
        />
      </div>
    </div>

    <div class="sizes">
      <strong>{{
        $t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT_DETAILS.SIZES')
      }}</strong>
      <p class="text-grey">{{ productSizes.join(' • ') }}</p>
    </div>

    <div v-if="productTypeInfos?.longDescription" class="description">
      <strong>{{
        $t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT_DETAILS.DESCRIPTION')
      }}</strong>
      <p class="text-grey" v-html="productTypeInfos.longDescription" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  CompositionSpecificCategoriesTree,
  CompositionSpecificCategoryProductType,
} from 'commons/composition/compositionCategoryHelper';
import { useCompositionStore } from 'commons/stores/composition';
import { computed, onBeforeMount, ref } from 'vue';
import { useStore } from 'vuex';
import compositionHelper from 'commons/composition/compositionHelper';
import ColorCircle from 'commons/colorCircle/ColorCircle.vue';
import shopService from 'commons/api/shopService/shopService';
import sprdCurrency from 'commons/currency/filter/currencyFilter';
import { getShopProductBasePrice } from '@spreadgroup/common-partner-lib/shop';
import { getProductType } from '@spreadgroup/common-partner-lib/composition';

interface Props {
  productTypeId: number;
  appearanceId?: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  select: [];
}>();

const store = useStore();
const compositionStore = useCompositionStore();
const compositionCategories = computed(
  () => compositionStore.categories as CompositionSpecificCategoriesTree,
);
const price = ref<{ amount: number; currencyId: number } | null>(null);

const productType = computed(
  (): CompositionSpecificCategoryProductType | undefined => {
    if (!compositionCategories.value) return undefined;
    return getProductType(compositionCategories.value, props.productTypeId) as
      | CompositionSpecificCategoryProductType
      | undefined;
  },
);

const selectedView = ref<string | null>(
  productType.value?.defaultViewId?.toString() || null,
);

const productTypeName = computed(
  () =>
    store.state.publishingCore.translations.productTypes[props.productTypeId]
      ?.name,
);

const appearanceNames = computed(
  () => store.state.publishingCore.translations.appearances,
);
const appearanceColors = computed(() => store.state.publishingCore.appearances);

const appearances = computed(() => {
  return (productType.value?.appearanceIds || [])
    .map((appearanceId) => ({
      id: appearanceId,
      name: appearanceNames.value[appearanceId]?.name || '',
      color: appearanceColors.value.find(
        (appearance) => appearance.id === appearanceId,
      )?.colors,
    }))
    .filter((appearance) => appearance.color && appearance.color.length > 0);
});

const productTypeInfos = computed(
  () =>
    store.state.publishingCore.translations.productTypes[props.productTypeId],
);

const productSizes = computed(
  () =>
    productType.value?.sizes.map(
      (sizeId) =>
        store.state.publishingCore.translations.sizes[sizeId]?.name || '',
    ) || [],
);

const images = computed(() => {
  return Object.keys(productTypeInfos.value?.views).map((viewId: string) => {
    return {
      viewId,
      img: `${compositionHelper.getProductTypeImage(
        props.productTypeId,
        Number(viewId),
        props.appearanceId || productType.value?.defaultAppearance || 0,
      )}?width=300`,
    };
  });
});

const selectedImage = computed(() => {
  return images.value.find(
    (image) => image.viewId.toString() === selectedView.value,
  );
});

const selectView = (viewId: string) => {
  selectedView.value = viewId;
};

onBeforeMount(async () => {
  const [{ productTypes }, mdsCommission] = await Promise.all([
    shopService.getProductTypes(store.state.user.shops[0].id),
    shopService.getMdsCommissions(
      store.state.user.data.id,
      store.state.user.shops[0].id,
    ),
  ]);

  const basePrice = getShopProductBasePrice(
    props.productTypeId,
    productTypes,
    mdsCommission,
  );

  if (basePrice) {
    price.value = basePrice;
  }
});
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/links';
@import 'src/scss/styleguide/type';
@import 'src/scss/constants';

.product-details {
  padding: 24px;
  background-color: $white;
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 0;
  max-height: 100%;
  overflow-y: auto;
  min-width: 350px;
  border: 1px solid $grey15;
  border-radius: $border-radius-medium;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  h2 {
    margin: 0;
  }
}

p {
  margin: 0;
}

.images {
  .main-image {
    padding: 4px;

    img {
      width: 100%;
    }
  }

  .alt-images {
    display: flex;
    gap: 8px;
    margin-top: 8px;
    overflow-x: auto;

    .alt-image {
      flex: 1;
      min-width: 50px;
      overflow: hidden;
      padding: 2px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      &.selected {
        border-color: $pa-color-main;
      }
    }
  }

  .alt-image,
  .main-image {
    display: flex;
    aspect-ratio: 1 / 1;
    border: 1px solid $grey10;
    border-radius: $border-radius-medium;
    background-color: $grey5;

    img {
      border-radius: $border-radius-small;
    }
  }
}

.meta-data {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .name,
  .price {
    margin: 0;
  }

  .price {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .brand {
    color: $grey65;
  }
}

.colors {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .color-circles {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }
}

.sizes,
.description {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.description {
  :deep(ul) {
    margin: 0 0 0 12px;
    padding: 0;
  }
}

.select-button {
  padding: 12px 16px;
}
</style>
